/**
 * Gameplay markers at both tiers (lifted from the dungeon-floors prototype's
 * `dungeon-markers.js`): the exit stair with its timber portal and hanging
 * lantern (only on an `exit` tile; the last floor has none), AP springs with
 * a stone basin, mineral-banded rim stones, a spout and rippling water, and
 * the boss's room: a three-layer dais with runes, braziers in the room's free
 * corners, a timber frame on the back wall with its seal, and inlaid plates
 * from the boss to the seal. Items lying on the floor are pixel billboards
 * (`billboards.ts`). Four batches: lit boxes and rounds, unlit glows (lantern
 * glass, runes, coals) and the water (its ripple shader at Medium). Every
 * location comes from the floor contract and the units; colours from the
 * theme (the prototype's fixed accent colours are shades of theme colours
 * here). A spring's parts are owned by its tile (`spring:x,y`) so
 * `spring_used` hides it; items are owned the same way (`item:x,y`).
 */
import type { Theme } from '@ab/content-schema';
import type { Floor, Unit } from '@ab/game-core';
import * as THREE from 'three';
import { PartBatch, part } from './batch.ts';
import { presentation } from './constants.ts';
import { inRoom, isOpen, tileIndex, tileRandom } from './grid.ts';
import { type Palette, shaded } from './palette.ts';

export type MarkerParts = { readonly boxes: PartBatch; readonly rounds: PartBatch; readonly glows: PartBatch; readonly water: PartBatch };

export const springOwner = (pos: { readonly x: number; readonly y: number }): string => `spring:${pos.x},${pos.y}`;
export const itemOwner = (pos: { readonly x: number; readonly y: number }): string => `item:${pos.x},${pos.y}`;

type Vec3 = readonly [number, number, number];
type Extra = Parameters<typeof part>[6];

/** The room of the boss, or the exit room. */
export const bossRoom = (floor: Floor, boss: Unit): Floor['rooms'][number] | undefined => floor.rooms.find((room) => inRoom(room, boss.pos.x, boss.pos.y)) ?? floor.rooms[floor.exitRoom];

/** The free corners of the boss's room where a brazier stands (`bossCorners`), with the offset toward the corner. */
export const bossCorners = (floor: Floor, units: readonly Unit[], r: Floor['rooms'][number]): { cx: number; cy: number; dx: number; dz: number }[] => {
  const inset = presentation.markerDetail.boss.cornerInset;
  const occupied = (x: number, y: number): boolean => [...floor.items, ...floor.springs, ...units].some((v) => v.pos.x === x && v.pos.y === y);
  const corners: [number, number][] = [
    [r.x, r.y],
    [r.x + r.w - 1, r.y],
    [r.x, r.y + r.h - 1],
    [r.x + r.w - 1, r.y + r.h - 1],
  ];
  return corners
    .filter(([x, y], i) => corners.findIndex(([a, b]) => a === x && b === y) === i && isOpen(floor, x, y) && !occupied(x, y))
    .map(([cx, cy]) => ({ cx, cy, dx: cx === r.x ? -inset : inset, dz: cy === r.y ? inset : -inset }));
};

export const buildMarkers = (floor: Floor, seed: number, theme: Theme, palette: Palette, units: readonly Unit[]): MarkerParts => {
  const boxes = new PartBatch('markers');
  const rounds = new PartBatch('marker-rounds');
  const glows = new PartBatch('marker-glows');
  const water = new PartBatch('marker-water');
  const k = presentation.marker;
  const d = presentation.markerDetail;
  const col = (hex: string): THREE.Color => new THREE.Color(hex);
  const mix = (a: THREE.Color, b: THREE.Color, t: number, factor = 1): THREE.Color => a.clone().lerp(b, t).multiplyScalar(factor);
  const exitTimber = col(theme.markers.exit.timber);
  const exitIron = col(theme.markers.exit.iron);
  const lanternLight = col(theme.markers.exit.light);
  const noise = (i: number, n: number): number => tileRandom(seed, i, n, 211);
  const box = (tile: number, at: Vec3, size: Vec3, tint: THREE.Color, extra: Extra = {}): void => {
    boxes.add(part(tile, at[0], at[1], at[2], size, tint, extra));
  };
  const glow = (tile: number, at: Vec3, size: Vec3, tint: THREE.Color, extra: Extra = {}): void => {
    glows.add(part(tile, at[0], at[1], at[2], size, tint, extra));
  };
  const round = (tile: number, at: Vec3, size: Vec3, tint: THREE.Color, extra: Extra = {}): void => {
    rounds.add(part(tile, at[0], at[1], at[2], size, tint, extra));
  };

  floor.tiles.forEach((type, i) => {
    if (type !== 'exit') return;
    const x = i % floor.width;
    const y = Math.floor(i / floor.width);
    const e = k.exit;
    const x2 = d.exit;
    const depth = theme.markers.exit.depthPercent / 100;
    const steps = theme.markers.exit.steps;
    const pit = palette.background;
    for (const side of [-1, 1]) box(i, [x + side * e.sill.offset, e.sill.y, -y], e.sill.size, palette.floor);
    box(i, [x, -depth - e.bottom.drop, -y], e.bottom.size, pit);
    for (const side of [-1, 1]) box(i, [x + side * x2.recess.offset, -depth / 2, -y], [x2.recess.width, depth, 1], mix(pit, palette.wall, x2.recess.wallMix));
    box(i, [x, -depth / 2, -y - e.rear.z], [e.rear.size[0], depth, e.rear.size[2]], pit);
    for (let n = 0; n < steps; n += 1) {
      const z = -y + 0.5 - ((n + 0.5) / steps) * e.step.run;
      const h = e.step.top - (n / steps) * depth;
      box(i, [x, h, z], [e.step.width, e.step.height, e.step.run / steps], shaded(palette.exit, e.step.shadeStart - (n / steps) * e.step.shadeRange));
      // A broad dark nosing stays a real band at one CSS pixel on a phone.
      box(i, [x, h + x2.nosing.lift, z + x2.nosing.forward / steps - x2.nosing.back], x2.nosing.size, mix(pit, palette.wall, x2.nosing.wallMix));
    }
    for (const side of [-1, 1]) {
      const px = x + side * e.post.offset;
      box(i, [px, e.post.size[1] / 2, -y - e.post.z], e.post.size, exitTimber);
      for (const h of e.band.heights) box(i, [px, h, -y - e.post.z], e.band.size, exitIron);
      box(i, [x + side * x2.grain.offset, x2.grain.y, -y - x2.grain.z], x2.grain.size, shaded(exitTimber, x2.grain.shade));
    }
    box(i, [x, e.lintel.y, -y - e.post.z], e.lintel.size, exitTimber);
    box(i, [x, x2.edge.y, -y - x2.edge.z], x2.edge.size, shaded(exitTimber, x2.edge.shade));
    // A lamp on the side, never a solid panel across the opening.
    const l = x2.lantern;
    const lx = x + l.x;
    const lz = -y - l.z;
    box(i, [lx, l.y - l.frameDrop, lz], [l.frame[0], l.height + l.frame[1], l.frame[2]], exitIron);
    glow(i, [lx, l.y, lz + l.glassForward], [l.glass[0], l.height, l.glass[1]], lanternLight);
    box(i, [lx, l.y, lz + l.mullionForward], [l.mullion[0], l.height + l.mullion[1], l.mullion[2]], exitIron);
    const s = x2.scars;
    for (let n = 0; n < s.count; n += 1) {
      const side = n % 2 ? 1 : -1;
      box(i, [x + side * (s.offset + noise(i, n) * s.jitter), s.y[0] + noise(i, n + 20) * s.y[1], -y - s.z], [s.size[0], s.height[0] + noise(i, n + 40) * s.height[1], s.size[1]], shaded(exitTimber, n % 3 ? s.dark : s.light));
    }
  });

  const stone = col(theme.markers.spring.stone);
  const rim = col(theme.markers.spring.rim);
  const ripple = col(theme.markers.spring.ripple);
  const waterColour = col(theme.markers.spring.water);
  for (const spring of floor.springs) {
    const { x, y } = spring.pos;
    const i = tileIndex(floor, x, y);
    const owner = springOwner(spring.pos);
    const s = k.spring;
    const sd = d.spring;
    round(i, [x, s.base.y, -y], s.base.size, stone, { owner });
    for (let n = 0; n < s.rim.stones; n += 1) {
      const a = (n * 2 * Math.PI) / s.rim.stones;
      const radial = s.rim.radius + (noise(i, n + 60) - 0.5) * sd.radialJitter;
      const h = s.rim.height[0] + tileRandom(seed, i, n, 7) * s.rim.height[1];
      const w = s.rim.width[0] + tileRandom(seed, i, n, 3) * s.rim.width[1];
      const sx = x + Math.sin(a) * radial;
      const sz = -y + Math.cos(a) * radial;
      box(i, [sx, h / 2 + s.rim.lift, sz], [w, h, s.rim.depth], shaded(n % 3 ? rim : stone, sd.shade[0] + noise(i, n) * sd.shade[1]), { ry: a, owner });
      // Mineral deposits break across some stones rather than capping every block.
      if (noise(i, n + 80) > sd.bandChance) {
        const offset = (noise(i, n + 100) - 0.5) * w * sd.bandOffset;
        const turn = (noise(i, n + 120) - 0.5) * sd.bandTurn;
        box(i, [sx + Math.cos(a) * offset, h + sd.bandLift, sz - Math.sin(a) * offset], [w * (sd.bandWidth[0] + noise(i, n + 140) * sd.bandWidth[1]), sd.bandHeight, sd.bandDepth[0] + noise(i, n + 160) * sd.bandDepth[1]], mix(rim, ripple, n % 3 ? sd.bandMix[0] : sd.bandMix[1]), { ry: a + turn, owner });
      }
      if (n % 2 === 0) box(i, [sx + Math.sin(a) * sd.chipOut, sd.chipY, sz + Math.cos(a) * sd.chipOut], [w * sd.chipWidth, sd.chipHeight, sd.chipDepth], shaded(stone, sd.chipShade), { ry: a + sd.chipTurn, owner });
    }
    water.add(part(i, x, s.water.y, -y, s.water.size, waterColour, { owner }));
    // A mineral spout on the rear rim, so the near edge stays open.
    const p = sd.spout;
    box(i, [x, p.y, -y - p.z], p.size, stone, { owner });
    box(i, [x, p.lipY, -y - p.lipZ], p.lip, mix(stone, ripple, p.lipMix), { owner });
    box(i, [x, p.flowY, -y - p.flowZ], p.flow, mix(waterColour, stone, p.flowMix), { owner });
    glow(i, [x, p.sourceY, -y - p.sourceZ], p.source, ripple, { owner });
  }

  for (const boss of units.filter((u) => u.boss !== null && !u.down)) {
    const { x, y } = boss.pos;
    const i = tileIndex(floor, x, y);
    const b = d.boss;
    const accent = col(theme.bossRoom.accent);
    const dark = mix(palette.background, accent, b.darkMix);
    const mid = mix(palette.wall, accent, b.midMix, b.midShade);
    if (theme.bossRoom.dais) {
      const dz = k.dais;
      round(i, [x, dz.base.y, -y], dz.base.size, dark);
      round(i, [x, dz.top.y, -y], dz.top.size, mid);
      round(i, [x, b.daisTop.y, -y], b.daisTop.size, shaded(mid, b.daisTop.shade));
      for (let n = 0; n < b.chunks; n += 1) {
        const a = (n * 2 * Math.PI) / b.chunks;
        const r = b.chunkRadius + (noise(i, n) - 0.5) * b.chunkJitter;
        box(i, [x + Math.sin(a) * r, b.chunkY, -y + Math.cos(a) * r], [b.chunk[0], b.chunk[1] + noise(i, n + 7) * b.chunkExtra, b.chunk[2]], n % 3 ? mid : dark, { ry: a });
        if (n % 3) box(i, [x + Math.sin(a) * b.chipRadius, b.chipY, -y + Math.cos(a) * b.chipRadius], b.chip, shaded(mid, b.chipShade), { ry: a });
      }
      for (let n = 0; n < dz.rune.count; n += 1) {
        const a = (n * 2 * Math.PI) / dz.rune.count;
        glow(i, [x + Math.sin(a) * dz.rune.radius, b.runeY, -y + Math.cos(a) * dz.rune.radius], dz.rune.size, accent, { ry: a });
        glow(i, [x + Math.sin(a) * b.notchRadius, b.runeY, -y + Math.cos(a) * b.notchRadius], b.notch, mix(accent, palette.exit, b.notchMix), { ry: a });
      }
    }
    const r = bossRoom(floor, boss);
    if (r === undefined) continue;
    // Braziers in the legal empty corners of the room.
    for (const { cx, cy, dx, dz } of bossCorners(floor, units, r)) {
      const t = tileIndex(floor, cx, cy);
      const px = cx + dx;
      const pz = -cy + dz;
      const z = b.brazier;
      round(t, [px, z.footY, pz], z.foot, dark);
      for (const side of [-1, 1]) {
        box(t, [px + side * z.legX, z.legY, pz], z.leg, exitTimber, { rz: -side * z.legTilt });
        box(t, [px + side * z.grainX, z.grainY, pz + z.grainZ], z.grain, shaded(exitTimber, z.grainShade));
      }
      round(t, [px, z.bowlY, pz], z.bowl, exitIron);
      round(t, [px, z.wellY, pz], z.well, shaded(palette.background, z.wellShade));
      for (let n = 0; n < 4; n += 1) {
        const a = (n * Math.PI) / 2;
        box(t, [px + Math.sin(a) * z.prongRadius, z.prongY, pz + Math.cos(a) * z.prongRadius], z.prong, exitIron);
        glow(t, [px + Math.sin(a) * z.coalRadius, z.coalY + noise(i, n + 90) * z.coalJitter, pz + Math.cos(a) * z.coalRadius], z.coal, n % 2 ? accent : mix(accent, palette.exit, z.coalMix), { ry: a });
      }
      box(t, [px, z.collarY, pz], z.collar, exitIron);
    }
    // The back-wall frame belongs to the room boundary; the approach in front stays open.
    const f = b.frame;
    const rearY = r.y + r.h - 1;
    const left = r.x + f.inset;
    const right = r.x + r.w - 1 - f.inset;
    if (right - left < f.minSpan) continue;
    const fz = -rearY - f.back;
    const tileAt = (px: number): number => tileIndex(floor, Math.round(px), rearY);
    for (const px of [left, right]) {
      box(tileAt(px), [px, f.postY, fz], f.post, exitTimber);
      for (const h of f.strapHeights) box(tileAt(px), [px, h, fz], f.strap, exitIron);
      for (let n = 0; n < 3; n += 1) box(tileAt(px), [px + (n - 1) * f.grainStep, f.grainY + n * f.grainRise, fz + f.grainForward], f.grain, shaded(exitTimber, n % 2 ? f.grainLight : f.grainDark));
    }
    const mx = (left + right) / 2;
    box(tileAt(mx), [mx, f.top, fz], [right - left + f.beamOverhang, f.beamHeight, f.beamDepth], exitTimber);
    box(tileAt(mx), [mx, f.top - f.edgeDrop, fz + f.edgeForward], [right - left + f.edgeOverhang, f.edgeSize[0], f.edgeSize[1]], shaded(exitTimber, f.grainLight));
    for (const side of [-1, 1]) {
      const px = side < 0 ? left + f.braceInset : right - f.braceInset;
      box(tileAt(px), [px, f.braceY, fz + f.braceForward], f.brace, shaded(exitTimber, f.braceShade), { rz: -side * f.braceTilt });
    }
    const sealX = Math.min(right - f.sealMargin, Math.max(left + f.sealMargin, x));
    box(tileAt(sealX), [sealX, f.sealY, fz + f.sealForward], f.seal, dark);
    glow(tileAt(sealX), [sealX, f.sealY + f.cutLift, fz + f.cutForward], f.cut, accent);
    for (const side of [-1, 1]) glow(tileAt(sealX), [sealX + side * f.notchOffset, f.sealY + f.cutLift, fz + f.cutForward], f.notch, accent, { rz: -side * f.notchTilt });
    // Low inlaid plates from the boss's side to the seal (no effect on any rule).
    const p = b.plates;
    const side = x < r.x + (r.w - 1) / 2 ? p.side : -p.side;
    const near = { x: Math.min(r.x + r.w - p.edgeFar, Math.max(r.x + p.edge, x + side)), y: Math.min(y + p.ahead, rearY - p.nearRear) };
    const far = { x: sealX, y: rearY - p.farRear };
    const length = Math.hypot(far.x - near.x, far.y - near.y);
    const count = Math.min(p.max, Math.ceil(length / p.spacing));
    for (let n = 0; n < count; n += 1) {
      const t = (n + 0.5) / count;
      const px = near.x + (far.x - near.x) * t;
      const py = near.y + (far.y - near.y) * t;
      const cx = Math.round(px);
      const cy = Math.round(py);
      if (!isOpen(floor, cx, cy) || [...floor.items, ...floor.springs].some((o) => o.pos.x === cx && o.pos.y === cy)) continue;
      const angle = Math.atan2(far.x - near.x, -(far.y - near.y));
      box(tileIndex(floor, cx, cy), [px, p.y, -py], p.plate, dark, { ry: angle });
      glow(tileIndex(floor, cx, cy), [px, p.seamY, -py], p.seam, accent, { ry: angle });
    }
  }
  return { boxes, rounds, glows, water };
};
